import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Tooltip } from '@mui/material'
import { formatTopologyPortTooltip, getEquipmentInfo, getNodeStatus } from '../utils'

const RADIUS = 16

const STATUS_COLORS = {
  up: { fill: '#CEE3B4', fillSelected: '#92BC62', stroke: '#587F31' },
  down: { fill: '#FEE2E2', fillSelected: '#E57373', stroke: '#DC2626' },
  missing_ref: { fill: '#E7E7E7', fillSelected: '#9CA3AF', stroke: '#5D5D5D' },
}

const HOVER_FILTER = [
  'drop-shadow(0px 1.85px 3.15px rgba(0,0,0,0.08))',
  'drop-shadow(0px 8.15px 6.52px rgba(0,0,0,0.04))',
  'drop-shadow(0px 20px 13px rgba(0,0,0,0.04))',
  'drop-shadow(0px 38.52px 25.48px rgba(0,0,0,0.07))',
].join(' ')

const TopologyNode = ({ nodeDatum, selectedMac, onSelectNode, onHoverNode, onUnhoverNode, toggleNode, nodeWidth = 280, hierarchyPointNode, onRevealChildren, onHideRevealedChildren, isChildrenForceRevealed, preview = false }) => {
  const [hovered, setHovered] = useState(false)
  const { t } = useTranslation()

  const isSelected = selectedMac === nodeDatum.mac_address
  const isVirtualRoot = nodeDatum.isVirtualRoot
  const status = isVirtualRoot ? 'missing_ref' : getNodeStatus(nodeDatum.is_reachable)
  const { fill, fillSelected, stroke } = STATUS_COLORS[status] || STATUS_COLORS.missing_ref
  const equipInfo = getEquipmentInfo(nodeDatum.type)
  const isCollapsed = Boolean(nodeDatum.__rd3t?.collapsed)
  const hasChildren = (nodeDatum.children?.length > 0) || (nodeDatum._children?.length > 0)
  const hasRealChildren = hasChildren || (nodeDatum.totalChildren ?? 0) > 0
  const allChildrenFiltered = hasRealChildren && !hasChildren
  const hasBranches = !isCollapsed && (nodeDatum.children?.length > 0)

  const linkIconX = -(nodeWidth / 2)
  const linkIconY = hierarchyPointNode?.parent
    ? (hierarchyPointNode.parent.x - hierarchyPointNode.x) / 2
    : 0

  const hasPosition = Boolean(nodeDatum.building_name)
  const locationIcon = (nodeDatum.positionless && !nodeDatum.is_main_switch)
    ? 'fa-regular fa-location-dot-slash'
    : (nodeDatum.isSameBuilding === false && hasPosition)
      ? 'fa-regular fa-building'
      : (nodeDatum.isSameFloor === false && nodeDatum.isSameBuilding === true && hasPosition)
        ? 'fa-regular fa-layer-group'
        : null

  const nameX = preview ? 0 : hasBranches ? -(RADIUS + 4) : RADIUS + 8
  const nameY = preview ? RADIUS + 16 : hasBranches ? RADIUS + 15 : -4
  const macX = preview ? 0 : hasBranches ? -(RADIUS + 4) : RADIUS + 8
  const macY = preview ? RADIUS + 28 : hasBranches ? RADIUS + 27 : 10
  const portTooltip = preview
    ? formatTopologyPortTooltip(nodeDatum.parentPort, nodeDatum.parentName)
    : ''

  const handleToggle = (e) => {
    e.stopPropagation()
    if (allChildrenFiltered) {
      onRevealChildren?.(nodeDatum.mac_address)
    } else if (isChildrenForceRevealed && hasChildren) {
      onHideRevealedChildren?.(nodeDatum.mac_address)
    } else {
      toggleNode?.()
    }
  }

  const badgeTransform = `translate(${RADIUS - 4}, ${-RADIUS + 4})`

  if (isVirtualRoot) {
    return (
      <g
        data-mac={nodeDatum.mac_address}
        onClick={() => onSelectNode?.(nodeDatum)}
        onMouseEnter={() => { setHovered(true); onHoverNode?.(nodeDatum) }}
        onMouseLeave={() => { setHovered(false); onUnhoverNode?.() }}
        style={{ cursor: 'pointer' }}
      >
        <circle
          r={RADIUS}
          fill={STATUS_COLORS.missing_ref.fill}
          stroke={hovered ? 'white' : STATUS_COLORS.missing_ref.stroke}
          strokeWidth={hovered ? 2 : 1.5}
          style={{ filter: hovered ? HOVER_FILTER : 'none' }}
        />
        <foreignObject x={-RADIUS + 2} y={-RADIUS + 2} width={(RADIUS - 2) * 2} height={(RADIUS - 2) * 2}>
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="fa-regular fa-grid-dividers fa-sm" style={{ color: '#464646' }} />
          </div>
        </foreignObject>
        {hasChildren && (
          <g onClick={handleToggle} transform={badgeTransform} style={{ cursor: 'pointer' }}>
            <circle r={9} fill="#464646" stroke="white" strokeWidth={2} />
            <text x={0} y={1} fill="white" stroke="none" fontSize={13} fontWeight={600} textAnchor="middle" dominantBaseline="middle">
              {isCollapsed ? '+' : '−'}
            </text>
          </g>
        )}
      </g>
    )
  }

  return (
    <g
      data-mac={nodeDatum.mac_address}
      onClick={() => onSelectNode?.(nodeDatum)}
      onMouseEnter={() => { setHovered(true); onHoverNode?.(nodeDatum) }}
      onMouseLeave={() => { setHovered(false); onUnhoverNode?.() }}
      style={{ cursor: 'pointer' }}
    >
      {isSelected && (
        <circle
          r={RADIUS + 4}
          fill="none"
          stroke={stroke}
          strokeWidth={1.5}
          opacity={0.3}
          style={{ animation: 'topo-pulse 1.2s ease-out infinite' }}
        />
      )}
      <circle
        r={RADIUS}
        fill={isSelected ? fillSelected : fill}
        stroke={hovered ? 'white' : stroke}
        strokeWidth={hovered ? 2 : 1.5}
        style={{ filter: hovered ? HOVER_FILTER : 'none' }}
      />
      <foreignObject x={-RADIUS + 2} y={-RADIUS + 2} width={(RADIUS - 2) * 2} height={(RADIUS - 2) * 2}>
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i
            className={`${equipInfo.icon} fa-sm`}
            style={{ color: isSelected ? '#fff' : stroke }}
          />
        </div>
      </foreignObject>

      {hasRealChildren && (
        <g onClick={handleToggle} transform={badgeTransform} style={{ cursor: 'pointer' }}>
          <circle r={9} fill="#464646" stroke="white" strokeWidth={2} />
          <text x={0} y={1} fill="white" stroke="none" fontSize={13} fontWeight={600} textAnchor="middle" dominantBaseline="middle">
            {(isCollapsed || allChildrenFiltered) ? '+' : '−'}
          </text>
        </g>
      )}

      {!preview && locationIcon && (
        <g transform={`translate(${linkIconX}, ${linkIconY})`}>
          <circle r={10} fill="white" stroke="#EBEBEB" strokeWidth={1} />
          <foreignObject x={-8} y={-8} width={16} height={16}>
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className={locationIcon} style={{ fontSize: 9, color: '#6b7280' }} />
            </div>
          </foreignObject>
        </g>
      )}

      {preview && hierarchyPointNode?.parent && (
        <Tooltip
          title={portTooltip}
          arrow
          placement="top"
          disableHoverListener={!portTooltip}
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: '#292929',
                color: '#fff',
                fontSize: 10,
                fontWeight: 500,
                borderRadius: '6px'
              }
            },
            arrow: { sx: { color: '#292929' } }
          }}
        >
          <g transform={`translate(${linkIconX}, 0)`}>
            <circle r={10} fill="white" stroke="#EBEBEB" strokeWidth={1} />
            <foreignObject x={-7} y={-7} width={14} height={14}>
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="fa-light fa-ethernet" style={{ fontSize: 9, color: '#656565' }} />
              </div>
            </foreignObject>
          </g>
        </Tooltip>
      )}

      <text x={nameX} y={nameY} textAnchor={preview ? 'middle' : undefined} fill="#111827" stroke="none" fontSize={preview ? 11 : 12} fontWeight={isSelected ? 600 : 500}>
        {nodeDatum.name || t('supervision.topology.unnamed')}
      </text>
      {nodeDatum.mac_address && (
        <text x={macX} y={macY} textAnchor={preview ? 'middle' : undefined} fill="#989898" stroke="none" fontSize={10} fontFamily="Consolas, monospace" fontWeight={isSelected ? 500 : 400}>
          {nodeDatum.mac_address}
        </text>
      )}
    </g>
  )
}

export default TopologyNode
